<template>
    <div id="ActivityFilterPc">
        <section id="activity-filter-location">
            <h2>
                行政區
                <span tabindex="0" role="button" @keypress.enter="resetLocation" @click="resetLocation">清除</span>
            </h2>
            <ElFormItem area-label="行政區">
                <div class="activity-filter-location-group" v-for="(item, index) of locationOptions" :key="'location' + index">
                    <el-checkbox
                        :id="'all-' + item.model"
                        :aria-label="item.title"
                        v-model="checkBox.checkAll[index]"
                        :indeterminate="checkBox.isIndeterminate[index]"
                        @change="checkBox.handleCheckAllChange(index) + updateRoute(item.model, queryItems[item.model])"
                        >{{ item.title }}</el-checkbox
                    >
                    <label :for="'all-' + item.model" class="visually-hidden">{{ item.title }}</label>
                    <el-checkbox-group
                        v-model="queryItems[item.model]"
                        @change="checkBox.handleCheckChange(index) + updateRoute(item.model, queryItems[item.model])"
                    >
                        <el-checkbox
                            v-for="city in item.options"
                            :key="city"
                            :label="city"
                            :aria-label="city"
                            :id="'checkbox-' + city"
                            >{{ city }}</el-checkbox
                        >
                        <label v-for="city in item.options" :key="'label-' + city" :for="'checkbox-' + city" class="visually-hidden">{{ city }}</label>
                    </el-checkbox-group>
                </div>
            </ElFormItem>
        </section>
        <section id="activity-filter-people-count">
            <h2>
                活動人數<span
                    tabindex="0"
                    role="button"
                    @keypress.enter="resetPeopleCount"
                    @click="resetPeopleCount"
                    >清除</span
                >
            </h2>
            <div class="slider-wrap" @click="sliderHandler">
                <ElSlider
                    v-model="sliderValue"
                    :max="200"
                    range
                    :step="10"
                    :marks="sliderMarks"
                ></ElSlider>
            </div>
        </section>
        <section id="activity-filter-date">
            <h2>
                活動日期
                <span
                    tabindex="0"
                    role="button"
                    @keypress.enter="resetDate"
                    @click="resetDate"
                    >清除</span
                >
            </h2>
            <ElForm
                ref="dateFormRef"
                :model="dateFormData"
                :rules="dateFormRule"
            >
                <ElFormItem label="起始日期" prop="startDate">
                    <ElDatePicker
                        v-model="dateFormData.startDate"
                        type="date"
                        placeholder="選擇起始日期"
                    ></ElDatePicker>
                </ElFormItem>
                <ElFormItem label="結束日期" prop="endDate">
                    <ElDatePicker
                        v-model="dateFormData.endDate"
                        placeholder="選擇結束日期"
                    ></ElDatePicker>
                </ElFormItem>
                <ElFormItem>
                    <div
                        class="small-btn"
                        role="button"
                        tabindex="0"
                        @click="submitForm"
                        @keypress.enter="submitForm"
                    >
                        篩選
                    </div>
                </ElFormItem>
            </ElForm>
        </section>
    </div>
</template>
<script>
export default {
    name: 'ActivityFilterPc',
}
</script>
<script setup>
import { ref, reactive, watch } from 'vue'
import { onBeforeRouteUpdate } from 'vue-router'
import moment from 'moment'
import { locationOptions } from '@/data/getActivityFilterOptions'
import { CheckBox } from '@/utils/checkbox.js'
import { initQueryItems, updateRoute } from '@/utils/query.js'
import { getStartDateRule, getEndDateRule } from '@/utils/form.js'
import {
    ElCheckbox,
    ElCheckboxGroup,
    ElForm,
    ElFormItem,
    ElDatePicker,
    ElSlider,
} from 'element-plus'

onBeforeRouteUpdate((to) => {
    initQueryItems(queryItems, to.query)
})

// 初始化 query 數據
const queryItems = reactive({
    locationN: [],
    locationM: [],
    locationS: [],
    locationE: [],
    locationO: [],
    minParticipateNum: 0,
    maxParticipateNum: 200,
    startDate: '',
    endDate: '',
})
initQueryItems(queryItems)

// 處理行政區 checkbox 連動效果
let checkBox = new CheckBox(queryItems)
checkBox.initCheck()

// 處理人數 slider
const sliderValue = ref([
    queryItems.minParticipateNum,
    queryItems.maxParticipateNum,
])
watch(sliderValue, async () => {
    await new Promise((res) => {
        setTimeout(() => res(), 500)
    })
    updateRoute(['minParticipateNum', 'maxParticipateNum'], sliderValue.value)
})

const sliderMarks = {
    0: '0',
    100: '100',
    200: '200',
}

// 處理日期校驗與送出
const dateFormRef = ref(null)
const dateFormData = reactive({
    startDate: queryItems.startDate || null,
    endDate: queryItems.endDate || null,
})
const dateFormRule = reactive({
    startDate: getStartDateRule(dateFormData),
    endDate: getEndDateRule(dateFormData),
})
const submitForm = () => {
    dateFormRef.value.validate((valid) => {
        if (valid) {
            // console.log(dateFormData)
            if (dateFormData.startDate && dateFormData.endDate) {
                updateRoute(
                    ['startDate', 'endDate'],
                    [
                        moment(dateFormData.startDate).format('YYYY/MM/DD'),
                        moment(dateFormData.endDate).format('YYYY/MM/DD'),
                    ]
                )
            } else if (dateFormData.startDate) {
                updateRoute(
                    ['startDate', 'endDate'],
                    [moment(dateFormData.startDate).format('YYYY/MM/DD'), '']
                )
            } else if (dateFormData.endDate) {
                updateRoute(
                    ['startDate', 'endDate'],
                    ['', moment(dateFormData.endDate).format('YYYY/MM/DD')]
                )
            } else {
                updateRoute(['startDate', 'endDate'])
            }
        } else {
            return false
        }
    })
}

// 清除 reset
function resetLocation() {
    queryItems.locationN = []
    queryItems.locationM = []
    queryItems.locationS = []
    queryItems.locationE = []
    queryItems.locationO = []
    for (let i = 0; i < 5; i++) {
        checkBox.isIndeterminate[i] = false
        checkBox.checkAll[i] = false
    }
    updateRoute([
        'locationN',
        'locationM',
        'locationS',
        'locationE',
        'locationO',
    ])
}
function resetPeopleCount() {
    queryItems.minParticipateNum = ''
    queryItems.maxParticipateNum = ''
    sliderValue.value = [0, 1000]
    updateRoute(['minParticipateNum', 'maxParticipateNum'])
}
function resetDate() {
    dateFormData.startDate = null
    dateFormData.endDate = null
    updateRoute(['startDate', 'endDate'])
}
</script>
<style lang="scss" scoped>
#ActivityFilterPc {
    margin-bottom: 50px;
    padding: 0 20px;
    width: 100%;
    box-sizing: border-box;
    border: 1px solid $theme1;
    section {
        padding: 30px 10px;
        border-bottom: 1px solid $theme1;
        &:last-child {
            border-bottom: none;
        }

        h2 {
            color: $gray2;
            font-size: 20px;
            font-weight: 700;
            line-height: 2em;
            span {
                margin-left: 1em;
                text-align: right;
                color: $grayA;
                font-size: 14px;
                line-height: 40px;
                cursor: pointer;
            }
        }
    }
}
#activity-filter-location:deep() {
    .activity-filter-location-group {
        margin: 10px 0;
    }
    .el-checkbox-group {
        margin-left: 30px;
    }
}
.slider-wrap {
    padding: 20px 10px 10px;
}
#activity-filter-date:deep() {
    .el-form {
        margin: 10px 0 0;
    }
    .el-date-editor.el-input {
        width: 150px;
    }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}

</style>
